'use client';

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Film, Chrome } from 'lucide-react';
import { useRouter } from 'next/navigation';
import {
  getAuth,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword
} from 'firebase/auth';
import { app as firebaseApp } from '@/lib/firebase';
import { toast as uiToast } from '@/hooks/use-toast';

const ALLOWED_EMAILS = [
  'myles@contibros.com',
  'karl@contibros.com',
  'lucas@contibros.com',
  'edit@xenon-post.com',
  'mylesconti@gmail.com',
  'matt.hearn.me@gmail.com',
  'dinusha@mac.com',
  'danfreene@gmail.com',
];

const checkAllowed = async (user: any, auth: any, router: any, toast: any) => {
  const email = user.email?.toLowerCase() || '';
  // Allow any email from @contibros.com domain
  if (email.endsWith('@contibros.com') || ALLOWED_EMAILS.includes(email)) {
    return true;
  }
  await auth.signOut();
  toast({ title: "Access Denied", description: "This email is not authorised to access Film Prep.", variant: "destructive" });
  return false;
};

export default function Auth() {
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const router = useRouter();
  const auth = getAuth(firebaseApp);
  const googleProvider = new GoogleAuthProvider();

  const toast = (options: { title: string, description?: string, variant?: "default" | "destructive" }) => {
    uiToast(options);
  };

  // Handle redirect result on page load
  useEffect(() => {
    const handleRedirectResult = async () => {
      try {
        setIsGoogleLoading(true);
        const result = await getRedirectResult(auth);
        if (result?.user) {
          const allowed = await checkAllowed(result.user, auth, router, toast);
          if (allowed) {
            toast({ title: "Sign-In Successful", description: `Welcome ${result.user.displayName || result.user.email}!` });
            router.push('/dashboard');
          }
        }
      } catch (error: any) {
        if (error.code && error.code !== 'auth/no-current-user') {
          console.error('Redirect result error:', error);
        }
      } finally {
        setIsGoogleLoading(false);
      }
    };
    handleRedirectResult();
  }, []);

  const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    const email = (event.currentTarget.elements.namedItem('login-email') as HTMLInputElement)?.value;
    const password = (event.currentTarget.elements.namedItem('login-password') as HTMLInputElement)?.value;

    if (!email || !password) {
      toast({ title: "Error", description: "Email and password are required.", variant: "destructive" });
      setIsLoading(false);
      return;
    }

    try {
      const loginResult = await signInWithEmailAndPassword(auth, email, password);
      const allowed = await checkAllowed(loginResult.user, auth, router, toast);
      if (allowed) router.push('/dashboard');
    } catch (error: any) {
      console.error('Login error:', error);
      toast({ title: "Login Failed", description: error.message || "Invalid credentials.", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    const email = (event.currentTarget.elements.namedItem('signup-email') as HTMLInputElement)?.value;
    const password = (event.currentTarget.elements.namedItem('signup-password') as HTMLInputElement)?.value;

    if (!email || !password) {
      toast({ title: "Error", description: "Email and password are required for signup.", variant: "destructive" });
      setIsLoading(false);
      return;
    }

    try {
      const signUpResult = await createUserWithEmailAndPassword(auth, email, password);
      const allowed = await checkAllowed(signUpResult.user, auth, router, toast);
      if (allowed) router.push('/dashboard');
    } catch (error: any) {
      console.error('Sign up error:', error.code, error.message);
      if (error.code === 'auth/email-already-in-use') {
        toast({
          title: "Sign Up Failed",
          description: "This email address is already in use. Please try logging in or use a different email.",
          variant: "destructive"
        });
      } else {
        toast({ title: "Sign Up Failed", description: error.message || "Could not create account.", variant: "destructive" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    try {
      // Try popup first — works on most modern browsers and deployed URLs
      const result = await signInWithPopup(auth, googleProvider);
      const allowed = await checkAllowed(result.user, auth, router, toast);
      if (allowed) {
        toast({ title: "Sign-In Successful", description: `Welcome ${result.user.displayName || result.user.email}!` });
        router.push('/dashboard');
      }
    } catch (popupError: any) {
      // If popup is blocked or fails, fall back to redirect
      if (
        popupError.code === 'auth/popup-blocked' ||
        popupError.code === 'auth/popup-closed-by-user' ||
        popupError.code === 'auth/cancelled-popup-request'
      ) {
        try {
          await signInWithRedirect(auth, googleProvider);
          // Page will redirect — result handled in useEffect above
        } catch (redirectError: any) {
          console.error('Google redirect error:', redirectError);
          toast({ title: "Google Sign-In Failed", description: redirectError.message || "Could not sign in with Google.", variant: "destructive" });
          setIsGoogleLoading(false);
        }
      } else if (popupError.code === 'auth/account-exists-with-different-credential') {
        toast({ title: "Account Already Exists", description: "An account with this email already exists. Please sign in with your email and password instead.", variant: "destructive" });
        setIsGoogleLoading(false);
      } else {
        console.error('Google Sign-In error:', popupError);
        toast({ title: "Google Sign-In Failed", description: popupError.message || "Could not sign in with Google.", variant: "destructive" });
        setIsGoogleLoading(false);
      }
    }
  };

  return (
    <Card className="w-full max-w-md shadow-lg">
      <CardHeader className="text-center">
        <div className="mb-4 flex justify-center">
          <img src="/logo.svg" alt="Film Prep Logo" className="h-10 w-10" />
        </div>
        <CardTitle className="text-2xl font-bold">Film Prep</CardTitle>
        <CardDescription>Your Film Production Collaboration Hub</CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="login">
          <TabsContent value="login">
            <form onSubmit={handleLogin}>
              <CardContent className="space-y-4 pt-6">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input id="login-email" name="login-email" type="email" placeholder="m@example.com" required disabled={isLoading || isGoogleLoading} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <Input id="login-password" name="login-password" type="password" required disabled={isLoading || isGoogleLoading} />
                </div>
              </CardContent>
              <CardFooter className="flex-col space-y-4">
                <Button type="submit" className="w-full" disabled={isLoading || isGoogleLoading}>
                  {isLoading ? 'Logging In...' : 'Login'}
                </Button>
                <Button variant="outline" type="button" className="w-full" onClick={handleGoogleSignIn} disabled={isLoading || isGoogleLoading}>
                  <Chrome className="mr-2 h-4 w-4" />
                  {isGoogleLoading ? 'Signing In...' : 'Sign in with Google'}
                </Button>
              </CardFooter>
            </form>
          </TabsContent>
          <TabsContent value="signup">
            <form onSubmit={handleSignUp}>
              <CardContent className="space-y-4 pt-6">
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input id="signup-email" name="signup-email" type="email" placeholder="m@example.com" required disabled={isLoading || isGoogleLoading} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <Input id="signup-password" name="signup-password" type="password" required disabled={isLoading || isGoogleLoading} />
                </div>
              </CardContent>
              <CardFooter className="flex-col space-y-4">
                <Button type="submit" className="w-full" disabled={isLoading || isGoogleLoading}>
                  {isLoading ? 'Signing Up...' : 'Sign Up'}
                </Button>
                <Button variant="outline" type="button" className="w-full" onClick={handleGoogleSignIn} disabled={isLoading || isGoogleLoading}>
                  <Chrome className="mr-2 h-4 w-4" />
                  {isGoogleLoading ? 'Signing In...' : 'Sign in with Google'}
                </Button>
              </CardFooter>
            </form>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
